import type { TFunction } from "i18next";
import isPes from "src/helpers/isPes";

type Filters = Record<string, unknown>;
type Item = Record<string, unknown>;

export type ActiveFilter = {
	key: string;
	label: string;
	values: string[];
	reset: Filters;
};

const list = (value: unknown) => (value || []) as Item[];

const flagLabels = (value: unknown, labels: Record<string, string>): string[] =>
	Object.entries(labels)
		.filter(([flag]) => (value as Filters | undefined)?.[flag])
		.map(([, label]) => label);

// jeden wpis na sekcję filtrów: etykieta chipa + wybrane wartości + czym ją wyczyścić
export function getActiveFilters(
	filters: Filters,
	defaults: Filters,
	t: TFunction,
	// etykiety z backendu, np. kategorie decków (value → label)
	labels: Record<string, string> = {},
): ActiveFilter[] {
	const reset = (...keys: string[]) =>
		Object.fromEntries(keys.map((key) => [key, defaults[key]]));
	const departments = list(filters.departments);
	const tags = list(filters.tags).filter(
		(tag) => !departments.some((d) => d.index === tag.dzialId),
	);
	const subtags = list(filters.subtags).filter(
		(subtag) => !list(filters.tags).some((tag) => tag.index === subtag.tagId),
	);
	const typeOfExam = filters.typeOfExam as string | null | undefined;

	const entries: (ActiveFilter | false)[] = [
		"departments" in defaults && {
			key: "departments",
			label: t("search.filters.chips.departments", "Działy"),
			values: [...departments, ...tags, ...subtags].map((item) =>
				String(item.title ?? ""),
			),
			reset: reset("departments", "tags", "subtags"),
		},
		"typeOfExam" in defaults && {
			key: "typeOfExam",
			label: t("search.filters.chips.typeOfExam", "Baza pytań"),
			values: typeOfExam
				? [
						t(
							`filters.exams.typeOfExam.${isPes() ? "pes" : "lek"}.${typeOfExam}.title`,
						),
					]
				: [],
			reset: reset("typeOfExam", "cemVersions"),
		},
		"cemVersions" in defaults && {
			key: "cemVersions",
			label: t("search.filters.chips.cemVersions", "Wersja bazy CEM"),
			values: list(filters.cemVersions).map(String),
			reset: reset("cemVersions"),
		},
		"terms" in defaults && {
			key: "terms",
			label: t("search.filters.chips.terms", "Terminy sesji"),
			values: list(filters.terms).map((term) =>
				term.year ? `${term.year} ${term.session ?? ""}`.trim() : "",
			),
			reset: reset("terms"),
		},
		"consultants" in defaults && {
			key: "consultants",
			label: t("search.filters.chips.consultants", "Konsultanci"),
			values: list(filters.consultants).map((c) => String(c.name ?? "")),
			reset: reset("consultants"),
		},
		"level" in defaults && {
			key: "level",
			label: t("search.filters.chips.level", "Poziom trudności"),
			values: list(filters.level).map((level) =>
				t(`filters.exams.level.levels.${level}`),
			),
			reset: reset("level"),
		},
		"additionalOption" in defaults && {
			key: "additionalOption",
			label: t("search.filters.chips.additionalOption", "Opcje"),
			values: flagLabels(filters.additionalOption, {
				starred: t("filters.exams.additional.starred"),
				wrongAnswers: t("filters.exams.additional.wrongAnswers"),
				skipCanceledQuestions: t(
					"filters.exams.additional.skipCanceledQuestions",
				),
				isNote: t("filters.exams.additional.questionWithNote"),
				isUnanswered: t("filters.exams.additional.unanswered"),
			}),
			reset: reset("additionalOption"),
		},
		"hasUnansweredComments" in defaults && {
			key: "hasUnansweredComments",
			label: t("filters.exams.unansweredComments.title", "Komentarze"),
			values: filters.hasUnansweredComments
				? [t("filters.exams.unansweredComments.label", "Bez odpowiedzi")]
				: [],
			reset: reset("hasUnansweredComments"),
		},
		"author" in defaults && {
			key: "author",
			label: t("search.filters.chips.author", "Autor"),
			values:
				filters.author && filters.author !== defaults.author
					? [
							labels[String(filters.author)] ??
								t(`decks.filter.${filters.author}`),
						]
					: [],
			reset: reset("author"),
		},
		"aiDiscussions" in defaults && {
			key: "aiDiscussions",
			label: t("filters.exams.aiDiscussions.title", "Omówienia AI"),
			values: flagLabels(filters.aiDiscussions, {
				isAiUnapproved: t("filters.exams.aiDiscussions.isAiUnapproved"),
				hasUnresolvedReviews: t(
					"filters.exams.aiDiscussions.hasUnresolvedReviews",
				),
			}),
			reset: reset("aiDiscussions"),
		},
	];

	return entries.filter(
		(entry): entry is ActiveFilter => !!entry && entry.values.length > 0,
	);
}
